import { assertNever, type NodeState } from "@reagent/sim";
import { gridToWorld } from "../iso/gridToWorld";
import { lookOf } from "../../reagents";
import { PALETTE } from "../palette";
import { useGameStore } from "../../app/gameStore";
import type { ForecastBadge } from "../hazardTells";
import { ReagentGlyph } from "./ReagentGlyph";
import { Dial, ForecastPin, PelletStack, ProgressArc } from "./parts";
import {
  CatalyticBed,
  FlowBuffer,
  FlowMover,
  Incubator,
  InputTank,
  ProductHopper,
  PhaseConverter,
  Reactor,
  Separator,
  ThermalRegulator,
  Vent,
  WasteBuyer,
} from "./pieces";

interface HardwareNodeProps {
  node: NodeState;
  /** Spec 0018: the earliest crossing the coming turn brings to this node. */
  forecast?: ForecastBadge | null;
  /** Spec 0024: outgoing edge ids from this node for valve routing. */
  outgoingEdgeIds?: readonly string[];
}

/**
 * Renders one node from a GridSnapshot. The switch is exhaustive over
 * NodeKind — adding a kind to the protocol without adding a mesh here is a
 * compile error, not a silently invisible piece of hardware.
 */
export function HardwareNode({
  node,
  forecast = null,
  outgoingEdgeIds = [],
}: HardwareNodeProps) {
  if (node.isDestroyed) return null;

  const [x, , z] = gridToWorld(node.pos);
  const heat = node.criticalTemp > 0 ? Math.min(node.heatCounter / node.criticalTemp, 1) : 0;
  // Buffer fullness stands in for "how busy this thing looks" — drives dials,
  // fill levels, and agitator speed.
  const activity = node.bufferCapacity > 0 ? Math.min(node.buffer.length / node.bufferCapacity, 1) : 0;
  // Capped Premium tank supply: show remaining units as a fill fraction.
  const supplyLevel =
    node.sourcing && node.supplyRemaining !== null && node.sourcing.options.premium.supplyCap
      ? node.supplyRemaining / node.sourcing.options.premium.supplyCap
      : null;

  return (
    <group position={[x, 0, z]}>
      <group rotation={[0, (node.rotation * Math.PI) / 180, 0]}>
        {renderPiece(node, activity, heat, outgoingEdgeIds, supplyLevel)}
      </group>
      {/* Contents sit outside the rotation: a machine may be turned, but what
          it is holding should always be readable from the camera. */}
      <NodeContents node={node} />
      <NodeHazardTells node={node} heat={heat} />
      {forecast !== null && <NodeForecastPin badge={forecast} />}
    </group>
  );
}

/** How high above a machine its contents float, and how far apart they sit. */
const CONTENTS_HEIGHT = 0.92;
const CONTENTS_SPACING = 0.17;
/** Beyond this the row would overhang neighbouring tiles, so it summarises. */
const CONTENTS_MAX = 4;

/**
 * What a machine is currently holding, floating above it.
 *
 * Buffer contents were previously invisible, which hid the two things this
 * game most often asks you to reason about: a reactor sitting on one precursor
 * waiting for its partner, and a packet standing still long enough to sludge
 * (`03-hazards.md` §2.5). "Nothing is happening" and "the wrong thing is happening" looked
 * identical, and neither had a tell.
 *
 * A hopper is exempt — it is a terminal sink whose contents are the quota
 * gauge's job, and a stage's whole output stacked over one tile is noise.
 */
function NodeContents({ node }: { node: NodeState }) {
  if ((node.kind === "productHopper" || node.kind === "wasteBuyer" || node.kind === "vent") || node.buffer.length === 0) return null;

  const shown = node.buffer.slice(0, CONTENTS_MAX);
  const offset = ((shown.length - 1) * CONTENTS_SPACING) / 2;

  return (
    <group position={[0, CONTENTS_HEIGHT, 0]}>
      {shown.map((packet, i) => {
        const look = lookOf(packet.composition);
        return (
          <group key={packet.id} position={[i * CONTENTS_SPACING - offset, 0, 0]}>
            <ReagentGlyph
              shape={look?.shape ?? "sphere"}
              // Sludge is spoiled material, so it stops reading as its reagent
              // and starts reading as the problem it now is (§2.5).
              color={node.isSludged ? PALETTE.sageDark : (look?.color ?? PALETTE.fluidSkyBlue)}
              size={0.055}
              opacity={node.isSludged ? 0.85 : 1}
            />
          </group>
        );
      })}
      {node.buffer.length > shown.length && (
        <mesh position={[offset + CONTENTS_SPACING, 0, 0]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshBasicMaterial color={PALETTE.chassisGreyDark} />
        </mesh>
      )}
    </group>
  );
}

/**
 * The three node-level hazard tells from `render/hazardTells.ts` — heat,
 * catalyst health, residence — each mounted at its own fixed corner so up to
 * all three can be up on one node (a heating catalyst bed sitting on a
 * waiting packet is exactly the pile-up spec 001 asks to keep legible) without
 * overlapping one another or the buffer contents floating at
 * `CONTENTS_HEIGHT`. Pressure and wall HP are edge hazards and live on `Pipe`.
 *
 * Gated on the same "away from resting" predicate `hazardTellsFor` uses
 * (heat 0, catalyst 100, residence 0) — read directly off the node here
 * rather than via that function, since a single `NodeState` already carries
 * every field the check needs except the sludge dwell limit, which comes off
 * the snapshot in the store.
 */
function NodeHazardTells({ node, heat }: { node: NodeState; heat: number }) {
  const sludgeResidenceTicks = useGameStore((s) => s.snapshot.limits.sludgeResidenceTicks);

  const showHeat = node.heatCounter !== 0;
  const showCatalyst = node.catalystHealth !== 100;
  const showResidence = node.residenceTicks !== 0;

  return (
    <group>
      {/* Mounted well above the tallest piece (the storage tank's tube tops out
          at y~0.85) rather than at chassis height — a corner low enough to sit
          beside a piece's own dial instead sits *inside* a chassis or tube on
          at least one node kind, since footprints and heights differ across
          the six pieces. Above everything is the one band guaranteed clear on
          all of them, same as `NodeContents` already relies on. */}
      {showHeat && <Dial position={[0.32, 1.05, 0.32]} value={heat} color={PALETTE.hazardWarning} />}
      {showCatalyst && (
        <PelletStack position={[-0.32, 0.85, 0.32]} filled={node.catalystHealth / 25} total={4} />
      )}
      {showResidence && (
        <ProgressArc
          position={[0, 1.35, 0]}
          fraction={sludgeResidenceTicks > 0 ? node.residenceTicks / sludgeResidenceTicks : 1}
        />
      )}
    </group>
  );
}

/**
 * Spec 0018's badge for a node. Planted a little above the residence arc — the
 * tallest of the three tells — so a node can be reading hot *and* forecast to
 * explode without the two readouts fighting for the same band.
 */
function NodeForecastPin({ badge }: { badge: ForecastBadge }) {
  const ticksPerTurn = useGameStore((s) => s.snapshot.ticksPerTurn);
  return <ForecastPin position={[0, 1.6, 0]} tickInTurn={badge.tickInTurn} ticksPerTurn={ticksPerTurn} />;
}

function renderPiece(
  node: NodeState,
  activity: number,
  heat: number,
  outgoingEdgeIds: readonly string[],
  supplyLevel: number | null = null,
) {
  switch (node.kind) {
    case "inputTank":
      return <InputTank activity={activity} heat={heat} supplyLevel={supplyLevel} />;
    case "reactor":
      return <Reactor activity={activity} heat={heat} />;
    case "flowMover":
      return (
        <FlowMover
          activity={activity}
          heat={heat}
          valveState={node.valveState}
          outgoingEdgeIds={outgoingEdgeIds}
          nodeId={node.id}
        />
      );
    case "catalyticBed":
      return <CatalyticBed activity={activity} heat={heat} catalystHealth={node.catalystHealth} />;
    case "flowBuffer":
      return <FlowBuffer activity={activity} heat={heat} />;
    case "productHopper":
      return <ProductHopper activity={activity} heat={heat} />;
    case "wasteBuyer":
      return <WasteBuyer activity={activity} heat={heat} />;
    case "thermalRegulator":
      return <ThermalRegulator activity={activity} heat={heat} />;
    case "separator":
      return <Separator activity={activity} heat={heat} />;
    case "phaseConverter":
      return <PhaseConverter activity={activity} heat={heat} />;
    case "incubator":
      return <Incubator activity={activity} heat={heat} />;
    case "vent":
      return <Vent activity={activity} />;
    default:
      return assertNever(node.kind, "HardwareNode");
  }
}
